/**
 * Turn a report page's URL into the view a saved alert watches.
 *
 * Pure, and deliberately React-free. The view is the address bar: `period`
 * and `weekStart` from the search, and a query of exactly the search entries
 * the report's catalogue entry lists. No page hands the sheet anything.
 */

import {
  dependsOnWeekStart,
  parsePeriodPresetId,
  PERIOD_VIEW_PARAMS,
  type NamedPeriodPresetId
} from './period-presets.ts'
import { FINANCE_WEEK_START, parseWeekStart } from './reporting-period.ts'
import { DRILL_PARAM } from './drill.ts'
import { LAYOUT_PARAM } from './table-layout-param.ts'
import {
  ALERT_WEEK_STARTS,
  type AlertReport,
  type AlertReportParams,
  type AlertReportWords,
  type AlertView
} from './alert-rules-api.ts'

/** A page's URL search, as the router parsed it. */
export type AlertSearch = Readonly<Record<string, unknown>>

/**
 * The parameters a saved view never stores in its query.
 *
 * The period and the week start have their own fields. `from` and `to` are
 * the days that period means today, and would freeze the alert on one range.
 * Paging is not part of a view. The catalogue does not list these, and this
 * list holds the rule if it ever does. `drill` is where the reader is, not
 * what an alert watches: a saved alert that stored it would reopen the sheet.
 */
const NEVER_SAVED: ReadonlySet<string> = new Set([
  ...PERIOD_VIEW_PARAMS,
  'from',
  'to',
  'page',
  'limit',
  LAYOUT_PARAM,
  DRILL_PARAM
])

/** What one saved entry is, so the chips can word it. */
export type AlertEntryKind = 'scope' | 'filter' | 'sort' | 'search'

/** One search entry a saved view keeps. */
export interface AlertViewEntry {
  param: string
  value: string
  kind: AlertEntryKind
}

/**
 * A search value as the text a query carries.
 *
 * The router parses `weekStart=1` as the number 1 and `activeOnly=false` as
 * the boolean. An object or an array is not a value this grammar has.
 *
 * @param value - One parsed search value.
 * @returns The text, or null when there is no usable one.
 */
export function searchText(value: unknown): string | null {
  if (typeof value === 'string') {
    return value === '' ? null : value
  }
  if (typeof value === 'number') {
    return Number.isFinite(value) ? String(value) : null
  }
  if (typeof value === 'boolean') {
    return String(value)
  }

  return null
}

/**
 * The search entries the catalogue lets a saved view keep, in query order:
 * the sort, the filters, then the scope.
 *
 * @param search - The page's URL search.
 * @param params - What the report's saved view may carry.
 * @returns The entries in force.
 */
export function alertViewEntries(
  search: AlertSearch,
  params: AlertReportParams
): AlertViewEntry[] {
  const entries: AlertViewEntry[] = []
  const add = (param: string, kind: AlertEntryKind): void => {
    const value = searchText(search[param])
    if (value !== null && !NEVER_SAVED.has(param)) {
      entries.push({ param, value, kind })
    }
  }
  if (params.sort) {
    add('sort', 'sort')
  }
  if (params.search) {
    add('search', 'search')
  }
  for (const param of params.filters) {
    add(param, 'filter')
  }
  for (const param of params.scope) {
    add(param, 'scope')
  }

  return entries
}

/**
 * The query string a saved view stores.
 *
 * @param search - The page's URL search.
 * @param params - What the report's saved view may carry.
 * @param dropped - Parameters the reader removed in the sheet.
 * @returns The query, or an empty string when nothing is set.
 */
function viewQuery(
  search: AlertSearch,
  params: AlertReportParams,
  dropped: readonly string[],
  words: Pick<AlertReportWords, 'switches'> = {}
): string {
  const saved = new URLSearchParams()
  for (const entry of alertViewEntries(search, params)) {
    if (!dropped.includes(entry.param)) {
      saved.append(entry.param, entry.value)
    }
  }
  for (const key of Object.keys(words.switches ?? {})) {
    if (
      dropped.includes(key) &&
      params.scope.includes(key) &&
      searchText(search[key]) !== 'false'
    ) {
      saved.set(key, 'false')
    }
  }

  return saved.toString()
}

/**
 * The saved view for one period and the page's URL.
 *
 * `weekStart` is stored only for the periods that read it. A URL that names
 * none means Finance's week, as it does on the page.
 *
 * @param search - The page's URL search.
 * @param params - What the report's saved view may carry.
 * @param period - The named period the alert will store.
 * @param dropped - Parameters the reader removed in the sheet.
 * @returns The view.
 */
export function alertView(
  search: AlertSearch,
  params: AlertReportParams,
  period: NamedPeriodPresetId,
  dropped: readonly string[] = [],
  words: Pick<AlertReportWords, 'switches'> = {}
): AlertView {
  const query = viewQuery(search, params, dropped, words)
  const weekStart = parseWeekStart(search.weekStart) ?? FINANCE_WEEK_START

  return dependsOnWeekStart(period)
    ? { period, weekStart: ALERT_WEEK_STARTS[weekStart], query }
    : { period, query }
}

/**
 * A period id the report offers and a saved alert may store.
 *
 * `custom` is refused here, not only by the endpoint: a fixed range would
 * send the same answer forever.
 *
 * @param value - A period id.
 * @param periods - The periods the report offers.
 * @returns The preset, or null.
 */
function offeredPeriod(
  value: unknown,
  periods: readonly string[]
): NamedPeriodPresetId | null {
  const parsed = parsePeriodPresetId(value)

  return parsed && parsed !== 'custom' && periods.includes(parsed)
    ? parsed
    : null
}

/**
 * The view for the period selected in the sheet, or null when it cannot be saved.
 *
 * @param search - The page's URL search.
 * @param report - The catalogue entry.
 * @param period - The select's value.
 * @param dropped - Parameters the reader removed.
 * @returns The view, or null.
 */
export function selectedView(
  search: AlertSearch,
  report: Pick<AlertReport, 'takesPeriod' | 'periods' | 'params' | 'words'>,
  period: string,
  dropped: readonly string[] = []
): AlertView | null {
  if (!report.takesPeriod) {
    return { query: viewQuery(search, report.params, dropped, report.words) }
  }
  const named = offeredPeriod(period, report.periods)

  return named
    ? alertView(search, report.params, named, dropped, report.words)
    : null
}

/**
 * The period the sheet starts on: the one in the URL, else the page's own.
 *
 * A page on its default period leaves it out of the URL, so the period the
 * page shows is the fallback. A `custom` range and a period this report does
 * not offer start the select empty. A saved alert needs a named period.
 *
 * @param search - The page's URL search.
 * @param periods - The periods the report offers.
 * @param pageDefault - The named period the page shows, from `pagePeriod`.
 * @returns The preset id, or an empty string.
 */
export function startingPeriod(
  search: AlertSearch,
  periods: readonly string[],
  pageDefault: string | null = null
): string {
  if (search.period !== undefined && search.period !== '') {
    return offeredPeriod(search.period, periods) ?? ''
  }

  return offeredPeriod(pageDefault, periods) ?? ''
}

/**
 * The page search a saved view opens on.
 *
 * The stored `weekStart` is a weekday name. The page reads `0`–`6`, Sunday
 * first, so the link and this search both write the number.
 *
 * @param view - A saved view.
 * @returns The search the page's address bar would hold.
 */
export function searchFromSavedView(view: AlertView): AlertSearch {
  const search: Record<string, unknown> = {}
  for (const [key, value] of new URLSearchParams(view.query)) {
    search[key] = value
  }
  if (view.period) {
    search.period = view.period
  }
  if (view.weekStart) {
    const index = ALERT_WEEK_STARTS.indexOf(view.weekStart)
    if (index >= 0) {
      search.weekStart = String(index)
    }
  }

  return search
}

/**
 * Say what the evaluator last did.
 *
 * @param state - The alert's state, or null before the first check.
 * @param format - Prints an instant in the reader's zone.
 * @returns One line for the card.
 */
export function describeState(
  state: { lastCheckedAt: string | null; lastSentAt: string | null } | null,
  format: (iso: string) => string
): string {
  if (!state?.lastCheckedAt) {
    return 'Not checked yet.'
  }
  const checked = `Last checked ${format(state.lastCheckedAt)}.`
  if (!state.lastSentAt) {
    return `${checked} Nothing sent yet.`
  }

  return `${checked} Last sent ${format(state.lastSentAt)}.`
}
